import {
  LayoutDashboard,
  KeyRound,
  BarChart3,
  Boxes,
  Plug2,
  Users as UsersIcon,
  MessageSquare,
  CircleDollarSign,
  Receipt,
  Ticket,
  Gauge,
  Image as ImageIcon,
  Timer,
  Settings as SettingsIcon,
  Wallet,
  User as UserIcon,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/stores/authStore'
import { useAppStore } from '@/stores/appStore'
import { useRole } from '@/hooks/useRole'
import { useSidebar, isSidebarModuleEnabled } from '@/hooks/useSidebar'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import { useLanguagePreference } from '@/hooks/useLanguagePreference'
import { logoutApi } from '@/api/user'
import { UserProvider } from '@/contexts/UserProvider'
import { useStatusStore } from '@/stores/statusStore'
import SidebarNav from '@/components/Layout/SidebarNav'
import ConsoleTopbar from '@/components/Layout/ConsoleTopbar'
import MobileNavDrawer from '@/components/Layout/MobileNavDrawer'
import { getUserDisplayName } from '@/lib/userDisplayName'

interface NavItem {
  key: string
  labelKey: string
  icon: JSX.Element
  section: string
  item?: string
  minRole?: number
  group: 'console' | 'personal' | 'admin'
}

const BRAND_NAME = (import.meta.env.VITE_APP_TITLE as string | undefined) || 'TokenTCP'

/** 仅当 `VITE_SHOW_MJ_NAV=true` 时显示侧栏「图片任务」；路由与页面源码仍保留 */
const SHOW_MJ_SIDEBAR = import.meta.env.VITE_SHOW_MJ_NAV === 'true'

const GROUP_LABEL_KEYS: Record<NavItem['group'], string> = {
  console: 'layout.console',
  personal: 'layout.account',
  admin: 'layout.management',
}

export default function Layout() {
  const { t, i18n } = useTranslation()
  useLanguagePreference()
  const navigate = useNavigate()
  const location = useLocation()
  const { isMobile } = useBreakpoint()
  const collapsed = useAppStore((s) => s.sidebarCollapsed)
  const toggleSidebar = useAppStore((s) => s.toggleSidebar)
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  const { user, logout } = useAuthStore()
  const { role, isRoot } = useRole()
  const modules = useSidebar()
  const status = useStatusStore((s) => s.status)

  useEffect(() => {
    setMobileNavOpen(false)
  }, [location.pathname])

  const handleLogout = async () => {
    try {
      await logoutApi()
    } finally {
      logout()
      navigate('/')
    }
  }

  const allItems: NavItem[] = useMemo(
    () => [
      { key: '/console', labelKey: 'nav.dashboard', icon: <LayoutDashboard size={16} />, section: 'console', item: 'detail', group: 'console' },
      {
        key: '/console/api-keys',
        labelKey: 'nav.apiKeys',
        icon: <KeyRound size={16} />,
        section: 'console',
        item: 'token',
        group: 'console',
      },
      { key: '/console/available-models', labelKey: 'nav.availableModels', icon: <Boxes size={16} />, section: 'console', item: 'availableModels', group: 'console' },
      { key: '/console/log', labelKey: 'nav.log', icon: <BarChart3 size={16} />, section: 'console', item: 'log', group: 'console' },
      { key: '/console/playground', labelKey: 'nav.playground', icon: <MessageSquare size={16} />, section: 'console', item: 'playground', group: 'console' },
      { key: '/console/midjourney', labelKey: 'nav.midjourney', icon: <ImageIcon size={16} />, section: 'console', item: 'midjourney', group: 'console' },
      { key: '/console/task', labelKey: 'nav.task', icon: <Timer size={16} />, section: 'console', item: 'task', group: 'console' },

      { key: '/console/personal', labelKey: 'nav.personal', icon: <UserIcon size={16} />, section: 'personal', item: 'personal', group: 'personal' },
      { key: '/console/topup', labelKey: 'nav.topup', icon: <Wallet size={16} />, section: 'personal', item: 'topup', group: 'personal' },

      { key: '/console/channel', labelKey: 'nav.channel', icon: <Plug2 size={16} />, section: 'admin', item: 'channel', minRole: 10, group: 'admin' },
      { key: '/console/user', labelKey: 'nav.user', icon: <UsersIcon size={16} />, section: 'admin', item: 'user', minRole: 10, group: 'admin' },
      { key: '/console/models', labelKey: 'nav.models', icon: <Boxes size={16} />, section: 'admin', item: 'model', minRole: 10, group: 'admin' },
      { key: '/console/deployment', labelKey: 'nav.deployment', icon: <Gauge size={16} />, section: 'admin', item: 'deployment', minRole: 10, group: 'admin' },
      { key: '/console/redemption', labelKey: 'nav.redemption', icon: <Ticket size={16} />, section: 'admin', item: 'redemption', minRole: 10, group: 'admin' },
      { key: '/console/topup-orders', labelKey: 'nav.topupOrders', icon: <Receipt size={16} />, section: 'admin', item: 'topupOrders', minRole: 10, group: 'admin' },
      { key: '/console/subscription', labelKey: 'nav.subscription', icon: <CircleDollarSign size={16} />, section: 'admin', item: 'subscription', minRole: 10, group: 'admin' },
      { key: '/console/setting', labelKey: 'nav.setting', icon: <SettingsIcon size={16} />, section: 'admin', item: 'setting', minRole: 100, group: 'admin' },
    ],
    []
  )

  const visibleItems = useMemo(() => {
    return allItems.filter((it) => {
      if (it.minRole && role < it.minRole) return false
      if (it.item === 'availableModels' && status?.pricing === false) return false
      if (it.item === 'midjourney' && !SHOW_MJ_SIDEBAR) return false
      return isSidebarModuleEnabled(modules, it.section, it.item)
    })
  }, [allItems, role, modules, status])

  const groupedItems = useMemo(() => {
    const groups: Array<{ group: NavItem['group']; label: string; items: Array<NavItem & { label: string }> }> = []
    const order: NavItem['group'][] = ['console', 'personal', 'admin']
    for (const g of order) {
      const items = visibleItems
        .filter((it) => it.group === g)
        .map((it) => ({ ...it, label: t(it.labelKey) }))
      if (items.length > 0) groups.push({ group: g, label: t(GROUP_LABEL_KEYS[g]), items })
    }
    return groups
  }, [visibleItems, t, i18n.language])

  const selectedMenuKey =
    [...visibleItems]
      .sort((a, b) => b.key.length - a.key.length)
      .find((item) => location.pathname.startsWith(item.key))?.key || '/console'

  const displayName = getUserDisplayName(user, t('layout.console'))

  const handleNavigate = (key: string) => {
    navigate(key)
    if (isMobile) setMobileNavOpen(false)
  }

  const collapseFooter = (
    <div className="border-t border-[var(--console-divider)] px-2 py-3 flex-shrink-0">
      <button
        type="button"
        className={`saas-collapse-btn ${collapsed ? 'is-collapsed' : ''}`}
        onClick={toggleSidebar}
        aria-label={collapsed ? t('layout.expandSidebar') : t('layout.collapseSidebar')}
      >
        {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
        {!collapsed && <span>{t('layout.collapseSidebar')}</span>}
      </button>
    </div>
  )

  const sidebarNavProps = {
    brandName: BRAND_NAME,
    groupedItems,
    selectedMenuKey,
    onNavigate: handleNavigate,
    onBrandClick: () => navigate('/'),
  }

  const isSettingPage = location.pathname === '/console/setting'

  return (
    <UserProvider>
      <div className="console-shell h-screen flex overflow-hidden">
        <aside
          className={`saas-sidebar hidden md:flex ${
            collapsed ? 'saas-sidebar-collapsed w-14' : 'w-52'
          } h-screen flex-col bg-[var(--console-sidebar-bg)] border-r border-[var(--console-border)]`}
        >
          <SidebarNav {...sidebarNavProps} collapsed={collapsed} footer={collapseFooter} />
        </aside>

        <MobileNavDrawer
          visible={mobileNavOpen}
          onClose={() => setMobileNavOpen(false)}
          brandName={BRAND_NAME}
          sidebarProps={sidebarNavProps}
        />

        <div className="flex-1 min-w-0 flex flex-col h-screen">
          <ConsoleTopbar
            user={user}
            isRoot={isRoot}
            displayName={displayName}
            onOpenMobileNav={() => setMobileNavOpen(true)}
            onLogout={handleLogout}
          />

          <main
            className={
              isSettingPage
                ? 'console-main console-main--setting flex-1 overflow-auto min-h-[calc(100vh-56px)]'
                : 'console-main flex-1 overflow-auto px-4 py-4 md:px-6 md:py-6 min-h-[calc(100vh-56px)]'
            }
          >
            <Outlet />
          </main>
        </div>
      </div>
    </UserProvider>
  )
}
